import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';
import '../styles/fiscal.css';
import '../styles/fiscalModerno.css';

// Fiscal → Certificados A1. O operador envia o .pfx + senha; o Saber abre o arquivo
// (senha errada é recusada), mostra validade/titular/CNPJ para conferência e só
// depois grava — arquivo cifrado no bucket, senha cifrada no banco. As senhas que
// vieram do ZADRA já estão aqui: quem tem "arquivo pendente" só precisa do .pfx.

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

const SITUACOES = {
  vencido: { cls: 'erro', ico: 'fa-circle-xmark', txt: 'Vencido', kpi: 'c-bad' },
  vence_30d: { cls: 'alerta', ico: 'fa-clock', txt: 'Vence em até 30 dias', kpi: 'c-warn' },
  arquivo_pendente: { cls: 'neutro', ico: 'fa-upload', txt: 'Arquivo pendente', kpi: 'c-navy' },
  sem_validade: { cls: 'alerta', ico: 'fa-calendar-xmark', txt: 'Sem validade', kpi: 'c-warn' },
  ok: { cls: 'ok', ico: 'fa-circle-check', txt: 'Em dia', kpi: 'c-ok' },
};

const fmtCnpj = (c) => (c && c.length === 14 ? c.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') : c || '—');
const semAcento = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const isoParaBr = (iso) => (iso ? iso.split('-').reverse().join('/') : '—');

export default function FiscalCertificados() {
  const [empresas, setEmpresas] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState('');            // situação
  const [semCadastro, setSemCadastro] = useState(false);
  const [envio, setEnvio] = useState(null);             // { empresa, arquivo, senha, conferencia, validade }
  const [processando, setProcessando] = useState(false);
  const [senhaVista, setSenhaVista] = useState({});     // { cod: senha }

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(`${API}/api/fiscal/certificados/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => { setEmpresas(r.data.empresas); setErro(''); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar os certificados'))
      .finally(() => setCarregando(false));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const contagem = useMemo(() => {
    const c = {};
    (empresas || []).forEach((e) => { if (e.certificado) c[e.certificado.situacao] = (c[e.certificado.situacao] || 0) + 1; });
    return c;
  }, [empresas]);

  const lista = useMemo(() => {
    const termo = semAcento(busca.trim());
    return (empresas || []).filter((e) => {
      if (!e.certificado && !semCadastro) return false;
      if (filtro && e.certificado?.situacao !== filtro) return false;
      return !termo || semAcento(e.cliente).includes(termo) || String(e.cod) === termo || (e.cnpj || '').includes(busca.replace(/\D/g, '') || '#');
    });
  }, [empresas, busca, filtro, semCadastro]);

  const abrirEnvio = (empresa) => setEnvio({ empresa, arquivo: null, senha: '', conferencia: null, validade: '' });

  const conferir = () => {
    const fd = new FormData();
    fd.append('arquivo', envio.arquivo);
    fd.append('senha', envio.senha);
    fd.append('conferir', '1');
    setProcessando(true); setErro('');
    axios.post(`${API}/api/fiscal/certificados/${envio.empresa.cod}/`, fd, { headers: headers() })
      .then((r) => setEnvio((s) => ({ ...s, conferencia: r.data.conferencia, validade: r.data.conferencia.validade })))
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível ler o certificado'))
      .finally(() => setProcessando(false));
  };

  const salvar = () => {
    const fd = new FormData();
    fd.append('arquivo', envio.arquivo);
    fd.append('senha', envio.senha);
    fd.append('validade', envio.validade);
    setProcessando(true); setErro('');
    axios.post(`${API}/api/fiscal/certificados/${envio.empresa.cod}/`, fd, { headers: headers() })
      .then((r) => { setEmpresas(r.data.empresas); setMsg(`${r.data.mensagem} — ${envio.empresa.cliente}`); setEnvio(null); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível salvar o certificado'))
      .finally(() => setProcessando(false));
  };

  const verSenha = (cod) => {
    if (senhaVista[cod] !== undefined) { setSenhaVista(({ [cod]: _, ...resto }) => resto); return; }
    axios.post(`${API}/api/fiscal/certificados/${cod}/senha/`, {}, { headers: headers() })
      .then((r) => setSenhaVista((s) => ({ ...s, [cod]: r.data.valor })))
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível mostrar a senha'));
  };

  const temSenhaGuardada = envio?.empresa?.certificado?.tem_senha;

  return (
    <div className="painel-op fz-app">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-certificate header-icon me-2" aria-hidden="true"></i>
            <h2 className="titulo-pagina m-0">Certificados A1</h2>
          </div>
          <div className="gr-toolbar">
            <button type="button" className="gr-btn" onClick={carregar} disabled={carregando}>
              <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
            </button>
          </div>
        </div>

        <div className="alert alert-light border small py-2">
          <i className="fas fa-circle-info me-2 text-primary"></i>
          Envie o arquivo <b>.pfx</b> com a senha: o Saber confere a senha, lê a validade e o CNPJ do titular e você confirma antes de salvar.
          O arquivo fica <b>cifrado</b> no Saber e substitui o anterior. Empresas com <b>arquivo pendente</b> já têm a senha (veio do ZADRA) — basta enviar o .pfx.
        </div>
        {erro && <div className="alert alert-danger py-2" role="button" onClick={() => setErro('')}><i className="fas fa-circle-exclamation me-2"></i>{erro}</div>}
        {msg && <div className="alert alert-success py-2" role="button" onClick={() => setMsg('')}><i className="fas fa-circle-check me-2"></i>{msg}</div>}

        <div className="po-kpis" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
          {Object.entries(SITUACOES).map(([chave, s]) => (
            <button key={chave} type="button" className={`po-kpi ${s.kpi} ${contagem[chave] ? '' : 'po-zero'}`}
              onClick={() => setFiltro((f) => (f === chave ? '' : chave))} style={filtro === chave ? { borderColor: '#00b5cc', boxShadow: '0 0 0 2px rgba(0,181,204,.35)' } : undefined}>
              <div className="po-kpi-rotulo"><i className={`fas ${s.ico}`}></i>{s.txt}</div>
              <div className="po-kpi-valor">{contagem[chave] || 0}</div>
            </button>
          ))}
        </div>

        <div className="fz-filtros">
          <input className="po-busca" placeholder="Buscar por nome, código ou CNPJ" value={busca} onChange={(e) => setBusca(e.target.value)} />
          <label className="po-toggle"><input type="checkbox" checked={semCadastro} onChange={(e) => setSemCadastro(e.target.checked)} />Mostrar empresas sem certificado</label>
          {filtro && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setFiltro('')}><i className="fas fa-xmark me-1"></i>limpar filtro: {SITUACOES[filtro].txt}</button>}
        </div>

        {carregando && !empresas ? (
          <div className="text-center py-5 text-muted"><i className="fas fa-spinner fa-spin fa-3x mb-3" style={{ color: '#00b5cc' }}></i><div className="fs-5">Carregando certificados...</div></div>
        ) : (
          <section className="po-card po-enter">
            <header className="po-card-head">
              <h3><i className="fas fa-certificate" aria-hidden="true"></i>Certificados por empresa</h3>
              <span className="badge bg-light text-dark">{lista.length}</span>
            </header>
            <div className="po-card-body">
              <div className="po-scroll" style={{ maxHeight: 'none', overflow: 'visible' }}>
                <table className="table table-hover po-table align-middle mb-0">
                  <thead><tr><th>Empresa</th><th>Situação</th><th>Validade</th><th>Titular do certificado</th><th>Enviado</th><th>Senha</th><th></th></tr></thead>
                  <tbody>
                    {lista.map((e) => {
                      const c = e.certificado;
                      const s = c ? SITUACOES[c.situacao] || SITUACOES.sem_validade : null;
                      return (
                        <tr key={e.cod}>
                          <td>
                            <div className="fw-semibold">{e.cliente}</div>
                            <div className="fz-sub fz-mono">{e.cod} · {fmtCnpj(e.cnpj)}</div>
                          </td>
                          <td>{s ? <span className={`fz-selo ${s.cls}`}><i className={`fas ${s.ico}`}></i>{s.txt}</span> : <span className="fz-selo neutro">sem cadastro</span>}</td>
                          <td className="fz-num">
                            {c?.validade || '—'}
                            {c?.dias != null && c.situacao !== 'arquivo_pendente' && <div className="fz-sub">{c.dias < 0 ? `venceu há ${-c.dias} dia(s)` : `${c.dias} dia(s)`}</div>}
                          </td>
                          <td className="small">{c?.titular || <span className="text-muted">—</span>}{c?.cnpj_titular && <div className="fz-sub fz-mono">{fmtCnpj(c.cnpj_titular)}</div>}</td>
                          <td className="small">{c?.enviado_em ? <>{c.enviado_em}<div className="fz-sub">{c.enviado_por || ''}</div></> : <span className="text-muted">—</span>}</td>
                          <td className="small" style={{ whiteSpace: 'nowrap' }}>
                            {c?.tem_senha ? (
                              <>
                                <span className={`fz-mono ${senhaVista[e.cod] === undefined ? 'fz-oculto' : ''}`}>{senhaVista[e.cod] === undefined ? '••••••' : senhaVista[e.cod]}</span>
                                <button className="btn btn-sm btn-link p-0 ms-2" title={senhaVista[e.cod] === undefined ? 'Mostrar' : 'Ocultar'} onClick={() => verSenha(e.cod)}>
                                  <i className={`fas ${senhaVista[e.cod] === undefined ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                                </button>
                              </>
                            ) : <span className="text-muted">—</span>}
                          </td>
                          <td className="text-end">
                            <button className="btn btn-sm btn-outline-primary" onClick={() => abrirEnvio(e)}>
                              <i className="fas fa-upload me-1"></i>{c?.situacao === 'arquivo_pendente' || !c ? 'Enviar .pfx' : 'Substituir'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {lista.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-4">Nenhum certificado com esses filtros.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {envio && (
          <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}><i className="fas fa-upload me-2" style={{ color: '#00b5cc' }}></i>Enviar certificado A1</h5>
                    <small style={{ color: '#6c757d' }}>{envio.empresa.cod} · {envio.empresa.cliente}</small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setEnvio(null)} disabled={processando}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label small fw-bold mb-1">Arquivo (.pfx ou .p12)</label>
                    <input type="file" accept=".pfx,.p12" className="form-control"
                      onChange={(ev) => setEnvio((s) => ({ ...s, arquivo: ev.target.files[0] || null, conferencia: null, validade: '' }))} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-bold mb-1">Senha do certificado</label>
                    <input type="password" className="form-control" autoComplete="new-password"
                      placeholder={temSenhaGuardada ? 'em branco = usar a senha já cadastrada' : ''}
                      value={envio.senha} onChange={(ev) => setEnvio((s) => ({ ...s, senha: ev.target.value, conferencia: null, validade: '' }))} />
                  </div>
                  {envio.conferencia && (
                    <div className="fz-conferencia">
                      <div className="fw-bold mb-2"><i className="fas fa-circle-check text-success me-2"></i>Senha confere. Confira os dados:</div>
                      <dl className="mb-2">
                        <dt>Titular</dt><dd>{envio.conferencia.titular || '—'}</dd>
                        <dt>CNPJ do titular</dt><dd className="fz-mono">{fmtCnpj(envio.conferencia.cnpj_titular)}</dd>
                        <dt>Validade lida do arquivo</dt><dd>{isoParaBr(envio.conferencia.validade)}</dd>
                      </dl>
                      {envio.conferencia.aviso && <div className="alert alert-warning py-2 small mb-2"><i className="fas fa-triangle-exclamation me-2"></i>{envio.conferencia.aviso}</div>}
                      <label className="form-label small fw-bold mb-1">Validade a gravar</label>
                      <input type="date" className="form-control" value={envio.validade} onChange={(ev) => setEnvio((s) => ({ ...s, validade: ev.target.value }))} />
                    </div>
                  )}
                </div>
                <div className="modal-footer bg-light">
                  <button className="btn btn-secondary me-auto" onClick={() => setEnvio(null)} disabled={processando}><i className="fas fa-times me-2"></i>Cancelar</button>
                  {!envio.conferencia ? (
                    <button className="btn btn-primary" onClick={conferir} disabled={processando || !envio.arquivo || (!envio.senha && !temSenhaGuardada)}>
                      <i className={`fas ${processando ? 'fa-spinner fa-spin' : 'fa-magnifying-glass'} me-2`}></i>Conferir certificado
                    </button>
                  ) : (
                    <button className="btn btn-primary" onClick={salvar} disabled={processando || !envio.validade}>
                      <i className={`fas ${processando ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>{processando ? 'Salvando...' : 'Salvar certificado'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
